import {
	type DefaultImageProps,
	type DefaultLinkProps,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	MenuDropdown
} from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";
import type React from "react";

import * as styles from "./UtilityLinks.module.scss";

interface Country<ImageComponentProps = DefaultImageProps> {
	flagImage: ImageProps<ImageComponentProps>;
	label: string;
	href: string;
}

interface ResponsiveLink<LinkComponentProps = DefaultLinkProps> {
	visibility: "desktop" | "mobile" | "both";
	link: LinkProps<LinkComponentProps>;
}

export interface UtilityLinksProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> {
	findLinks: ResponsiveLink<LinkComponentProps>[];
	knowledgeCenter?: LinkProps<LinkComponentProps>;
	accountLinks: LinkProps<LinkComponentProps>[];
	accountMenuLabel?: string;
	countryList: Country<ImageComponentProps>[];
	currentCountryImage: ImageProps<ImageComponentProps>;
	onMyShowsClick?: () => void;
}

/**
 * Header Utility Links component
 */
export function UtilityLinks<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>({
	findLinks,
	knowledgeCenter,
	accountLinks,
	accountMenuLabel,
	countryList,
	currentCountryImage,
	onMyShowsClick
}: UtilityLinksProps<LinkComponentProps, ImageComponentProps>): JSX.Element {
	const mobileFindLinks = findLinks.filter(
		({ visibility }) => visibility !== "desktop"
	);
	const hasAccountDropdown = !!accountMenuLabel && accountLinks.length > 0;

	return (
		<div className={styles["utility-links"]}>
			<div
				{...classNameHelper(styles["container"], styles["find-links"], {
					[styles["mobile-centered"]]: mobileFindLinks.length < 2
				})}
			>
				{findLinks.map(({ visibility, link }, idx) => {
					const isMyShows =
						onMyShowsClick &&
						typeof link.children === "string" &&
						link.children.toLowerCase().includes("my shows");

					return (
						<Link<LinkComponentProps>
							key={idx}
							{...link}
							href={isMyShows ? undefined : link.href}
							{...classNameHelper(styles["link"], {
								[styles[visibility]]: !!visibility
							})}
							kind="xxsl"
							onClick={
								isMyShows
									? (e) => {
											e.preventDefault();
											onMyShowsClick?.();
										}
									: link.onClick
							}
							onPress={
								isMyShows
									? () => {
											onMyShowsClick?.();
										}
									: link.onPress
							}
						/>
					);
				})}
			</div>

			{knowledgeCenter && (
				<div
					{...classNameHelper(
						styles["container"],
						styles["knowledge-center"]
					)}
				>
					<Link<LinkComponentProps>
						{...knowledgeCenter}
						{...classNameHelper(styles["link"])}
						kind="xxsl"
					/>
				</div>
			)}

			<div
				{...classNameHelper(
					styles["container"],
					styles["account-links"]
				)}
			>
				{hasAccountDropdown ? (
					<MenuDropdown
						className={styles["account-dropdown"]}
						triggerClassName={styles["account-dropdown-button"]}
						// DS v4 MenuDropdown reads each item as
						// MenuItemProps (`{ label, href, ... }`). Map our
						// LinkProps `{ children, href }` shape into it so
						// items render with visible labels.
						items={
							accountLinks.map((link) => ({
								...link,
								label: link.children as string | undefined,
								href: (link as unknown as { href?: string })
									.href
							})) as unknown as React.ComponentProps<
								typeof MenuDropdown
							>["items"]
						}
						icon={{ name: "ChevronDown" }}
						expandedIcon={{ name: "ChevronUp" }}
					>
						{accountMenuLabel}
					</MenuDropdown>
				) : (
					accountLinks.map((accountLink, idx) => (
						<Link<LinkComponentProps>
							key={idx}
							{...accountLink}
							{...classNameHelper(styles["link"])}
							kind="xxsl"
						/>
					))
				)}

				<MenuDropdown
					className={styles["locale-dropdown"]}
					triggerClassName={styles["locale-dropdown-button"]}
					// DS v4 MenuDropdown renders `item.label` (text only),
					// not arbitrary children. Use the plain country label
					// here — the DS v4 MenuItem doesn't accept JSX children,
					// so the flag image in the menu item is not shown.
					items={
						countryList.map((country) => ({
							href: country.href,
							label: country.label
						})) as unknown as React.ComponentProps<
							typeof MenuDropdown
						>["items"]
					}
				>
					Shopping In:
					<Image<ImageComponentProps>
						{...currentCountryImage}
						{...classNameHelper(
							styles["locale-image"],
							currentCountryImage.className
						)}
						alt={currentCountryImage.alt || "Flag Logo"}
					/>
				</MenuDropdown>
			</div>
		</div>
	);
}
